import { clone, create, equals, fromJson, toJson, type JsonObject } from "@bufbuild/protobuf";
import { FieldMaskSchema, type FieldMask } from "@bufbuild/protobuf/wkt";
import deepEqual from "deep-equal";
import { readable, writable, type Readable, type Writable } from "svelte/store";
import type { VersionIncrement } from "#gen/asset/v1/asset_pb.js";
import {
	AdUnitSchema,
	AgentSchema,
	AssetSchema,
	AssetType,
	BundleSchema,
	ConversionType,
	DecisionSchema,
	EntryPointBatchRefSchema,
	ExitConfigSchema,
	ExperimentSchema,
	ExternalDestinationSchema,
	LandingPageSchema,
	LookupSchema,
	ModuleSchema,
	PageContainerSchema,
	PolicyRefSchema,
	StaticAssetRefSchema,
	SuiteRefSchema,
	SuiteSchema,
	type AdUnit,
	type AdUnitConfig,
	type AdUnitConfigJson,
	type AdUnitJson,
	type Agent,
	type AgentJson,
	type ApiLookup,
	type Asset,
	type AssetJson,
	type Bundle,
	type Data,
	type Decision,
	type DecisionJson,
	type Destination,
	type EntryPoint,
	type EntryPointBatchRef,
	type EntryPointBatchRefJson,
	type ExitConfig,
	type ExitConfigJson,
	type Experiment,
	type ExperimentJson,
	type ExternalDestination,
	type ExternalDestinationJson,
	type Header,
	type LandingPage,
	type LandingPageJson,
	type Lookup,
	type LookupJson,
	type Module,
	type ModuleTemplate,
	type PageContainer,
	type PageContainerJson,
	type PolicyRef,
	type PolicyRefJson,
	type RenderMode,
	type StaticAssetRef,
	type StaticAssetRefJson,
	type Suite,
	type SuiteRef,
	type SuiteRefJson,
} from "#gen/bundles/v1/bundle_pb.js";
import { GeoLocationSchema } from "#gen/bundles/v1/common_pb.js";
import type { ConversionMetaData } from "#gen/bundles/v1/events_pb.js";
import { BundleService, Mode, RequestIntent, type BulkImportSuitesRequest, type Category, type CloneOptions, type DeleteSuiteRequest, type UpdateSuiteRequest } from "#gen/bundles/v1/service_pb.js";
import { BatchSchema, CodeSchema, type Batch, type BatchJson, type Code, type CodeJson } from "#gen/codes/v3/codes_pb.js";
import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { EntrypointBatchSchema, type EntrypointBatch, type EntrypointBatchJson } from "#gen/entrypoints/v1/entrypoints_pb.js";
import type { Folder } from "#gen/folder/v1/folders_pb.js";
import { PolicySchema, type Policy, type PolicyJson } from "#gen/legal/v1/legal_pb.js";
import type { Page, PageJson } from "#gen/pages/v2/page_pb.js";
import type { ShortUrl } from "#gen/shorturls/v1/shorturls_pb.js";
import { StaticAssetSchema, type StaticAsset, type StaticAssetJson } from "#gen/staticassets/v1/static_assets_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { APIError, BulkError, handleError } from "#lib/api/error.js";
import type { Filter } from "#lib/api/pagination.js";
import { deepClone } from "#lib/deepClone.js";
import { genericize } from "#lib/protobuf-compat.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";
import type { GeolocationCoords } from "svelte-geolocation/Geolocation.svelte";

const MAX_BULK_SIZE_PER_UPDATE = 50;

export interface ValidationResult {
	valid: boolean;
	errors: Record<string, string>;
}

export interface SuiteList {
	suites: Suite[];
	pageInfo?: PaginationPageInfo;
}

export interface BundleList {
	bundles: Bundle[];
	pageInfo?: PaginationPageInfo;
}

export interface ModuleTemplateList {
	templates: ModuleTemplate[];
	pageInfo?: PaginationPageInfo;
}

export enum Visibilities {
	PRIVATE = "VISIBILITY_PRIVATE",
	PUBLIC = "VISIBILITY_PUBLIC",
	ORG = "VISIBILITY_ORG",
	INTERNAL = "VISIBILITY_INTERNAL",
}

export interface CallResponse {
	statusCode: number;
	responseHeaders: Header[];
	responseBody: string;
}

export enum ArchiveReason {
	USER_ARCHIVAL = "ARCHIVE_REASON_USER_ARCHIVAL",
	SUBSCRIPTION_CANCELLED = "ARCHIVE_REASON_SUBSCRIPTION_CANCELLED",
	CONTENT_MODERATION = "ARCHIVE_REASON_CONTENT_MODERATION",
	ML_MODERATION = "ARCHIVE_REASON_ML_MODERATION",
	FOLDER_ARCHIVED = "ARCHIVE_REASON_FOLDER_ARCHIVED",
	PARENT_ARCHIVED = "ARCHIVE_REASON_PARENT_ARCHIVED",
}

export const CUSTOM_TEMPLATE_CATEGORY = "Custom";
export const BETA_TEMPLATE_CATEGORY = "Beta";
export const EXTERNAL_VISIBILITIES = [Visibilities.PRIVATE, Visibilities.PUBLIC, Visibilities.ORG];

export enum AssetState {
	DRAFT = "ASSET_STATE_DRAFT",
	ACTIVE = "ASSET_STATE_ACTIVE",
	ARCHIVED = "ASSET_STATE_ARCHIVED",
}

export type AssetLoadJson = {
	asset: AssetJson;
	data?: JsonObject;
	result?: JsonObject;
};

export type ConfigType = PageJson | CodeJson | LandingPageJson | ExternalDestinationJson | BatchJson | EntrypointBatchJson | EntryPointBatchRefJson | DecisionJson | ExperimentJson | SuiteRefJson | PageContainerJson | StaticAssetRefJson | StaticAssetJson | PolicyRefJson | PolicyJson | AgentJson | ExitConfigJson | LookupJson | AdUnitJson | AdUnitConfigJson | JsonObject;
type StoreType = Page | LandingPage | PageContainer | Code | ExternalDestination | Batch | ShortUrl | EntrypointBatch | EntryPointBatchRef | Experiment | SuiteRef | StaticAssetRef | StaticAsset | PolicyRef | Policy | Agent | ExitConfig | Lookup | AdUnit | AdUnitConfig;

export class AssetLoad {
	private _asset: Asset;
	private _data?: JsonObject;
	private _result?: JsonObject;

	private store: Writable<StoreType> | undefined;
	private dataStore: Writable<JsonObject | undefined>;
	private resultStore: Readable<JsonObject | undefined>;

	constructor(asset: Asset, data?: JsonObject, result?: JsonObject) {
		this._asset = asset;
		this._data = data;
		this._result = result;
		this.store = writable(asset.data.value as StoreType);
		this.dataStore = writable(data);
		this.resultStore = readable(result);
	}

	key(): string {
		return this._asset.key;
	}

	requestedKey(): string | undefined {
		return this._asset.requestedKey;
	}

	resolvedKey(): string {
		return this.requestedKey() ?? this.key();
	}

	conversionType(): ConversionType {
		return this._asset.conversionType;
	}

	conversionComponent(): string {
		return this._asset.conversionComponent;
	}

	renderMode(): RenderMode {
		return this._asset.renderMode;
	}

	shortName(): string {
		return this._asset.shortName;
	}

	displayName(): string {
		return this._asset.displayName;
	}

	iconUrl(): string {
		return this._asset.iconUrl;
	}

	context(): Record<string, string> | undefined {
		return this._asset.context as Record<string, string> | undefined;
	}

	assetType(): AssetType {
		switch (this._asset.data.case) {
			case "code":
				return AssetType.CODE;
			case "codeV3":
				return AssetType.CODE_V3;
			case "page":
				return AssetType.PAGE;
			case "pageV2":
				return AssetType.PAGE_V2;
			case "landingPage":
				return AssetType.LANDING_PAGE;
			case "externalDestination":
				return AssetType.EXTERNAL_DESTINATION;
			case "shortUrl":
				return AssetType.SHORT_URL;
			case "codeBatch":
				return AssetType.CODE_BATCH;
			case "entrypointBatch":
				return AssetType.ENTRYPOINT_BATCH;
			case "entrypointBatchRef":
				return AssetType.ENTRYPOINT_BATCH_REF;
			case "experiment":
				return AssetType.EXPERIMENT;
			case "decision":
				return AssetType.DECISION;
			case "pageContainer":
				return AssetType.PAGE_CONTAINER;
			case "suiteRef":
				return AssetType.SUITE_REF;
			case "staticAsset":
				return AssetType.STATIC_ASSET;
			case "staticAssetRef":
				return AssetType.STATIC_ASSET_REF;
			case "policy":
				return AssetType.POLICY;
			case "policyRef":
				return AssetType.POLICY_REF;
			case "agent":
				return AssetType.AGENT;
			case "module":
				return AssetType.MODULE;
			case "lookup":
				return AssetType.LOOKUP;
			case "adUnit":
				return AssetType.AD_UNIT;
			case "exit":
				return AssetType.EXIT;
			case "goto":
				return AssetType.GOTO;
		}
		return AssetType.UNSPECIFIED;
	}

	config(): ConfigType {
		switch (this.assetType()) {
			case AssetType.PAGE_V2:
				return (this._asset.data.value as Page).config as PageJson;
			case AssetType.LANDING_PAGE:
				return toJson(LandingPageSchema, this._asset.data.value as LandingPage) as LandingPageJson;
			case AssetType.EXTERNAL_DESTINATION:
				return toJson(ExternalDestinationSchema, this._asset.data.value as ExternalDestination) as ExternalDestinationJson;
			case AssetType.CODE_V3:
				return toJson(CodeSchema, this._asset.data.value as Code) as CodeJson;
			case AssetType.CODE_BATCH:
				return toJson(BatchSchema, this._asset.data.value as Batch) as BatchJson;
			case AssetType.ENTRYPOINT_BATCH:
				return toJson(EntrypointBatchSchema, this._asset.data.value as EntrypointBatch) as EntrypointBatchJson;
			case AssetType.ENTRYPOINT_BATCH_REF:
				return toJson(EntryPointBatchRefSchema, this._asset.data.value as EntryPointBatchRef) as EntryPointBatchRefJson;
			case AssetType.DECISION:
				return toJson(DecisionSchema, this._asset.data.value as Decision) as DecisionJson;
			case AssetType.EXPERIMENT:
				return toJson(ExperimentSchema, this._asset.data.value as Experiment) as ExperimentJson;
			case AssetType.PAGE_CONTAINER:
				return toJson(PageContainerSchema, this._asset.data.value as PageContainer) as PageContainerJson;
			case AssetType.SUITE_REF:
				return toJson(SuiteRefSchema, this._asset.data.value as SuiteRef) as SuiteRefJson;
			case AssetType.STATIC_ASSET:
				return toJson(StaticAssetSchema, this._asset.data.value as StaticAsset) as StaticAssetJson;
			case AssetType.STATIC_ASSET_REF:
				return toJson(StaticAssetRefSchema, this._asset.data.value as StaticAssetRef) as StaticAssetRefJson;
			case AssetType.POLICY_REF:
				return toJson(PolicyRefSchema, this._asset.data.value as PolicyRef) as PolicyRefJson;
			case AssetType.POLICY:
				return toJson(PolicySchema, this._asset.data.value as Policy) as PolicyJson;
			case AssetType.AGENT:
				return toJson(AgentSchema, this._asset.data.value as Agent) as AgentJson;
			case AssetType.EXIT:
				return toJson(ExitConfigSchema, this._asset.data.value as ExitConfig) as ExitConfigJson;
			case AssetType.LOOKUP:
				return toJson(LookupSchema, this._asset.data.value as Lookup) as LookupJson;
			case AssetType.AD_UNIT:
				return toJson(AdUnitSchema, this._asset.data.value as AdUnit) as AdUnitJson;
		}
		return {};
	}

	data(): Writable<JsonObject | undefined> {
		return this.dataStore;
	}

	result(): Readable<JsonObject | undefined> {
		return this.resultStore;
	}

	asset(): Asset {
		return this._asset;
	}

	asPage(): Writable<Page> | undefined {
		if (this._asset.data.case === "pageV2") {
			return this.store as Writable<Page>;
		}
		return undefined;
	}

	asExit(): Writable<ExitConfig> | undefined {
		if (this._asset.data.case === "exit") {
			return this.store as Writable<ExitConfig>;
		}
		return undefined;
	}

	asLandingPage(): Writable<LandingPage> | undefined {
		if (this._asset.data.case === "landingPage") {
			return this.store as Writable<LandingPage>;
		}
		return undefined;
	}

	asPageContainer(): Writable<PageContainer> | undefined {
		if (this._asset.data.case === "pageContainer") {
			return this.store as Writable<PageContainer>;
		}
		return undefined;
	}

	asSuiteRef(): Writable<SuiteRef> | undefined {
		if (this._asset.data.case === "suiteRef") {
			return this.store as Writable<SuiteRef>;
		}
		return undefined;
	}

	asStaticAsset(): Writable<StaticAsset> | undefined {
		if (this._asset.data.case === "staticAsset") {
			return this.store as Writable<StaticAsset>;
		}
		return undefined;
	}

	asStaticAssetRef(): Writable<StaticAssetRef> | undefined {
		if (this._asset.data.case === "staticAssetRef") {
			return this.store as Writable<StaticAssetRef>;
		}
		return undefined;
	}

	asCode(): Writable<Code> | undefined {
		if (this._asset.data.case === "codeV3") {
			return this.store as Writable<Code>;
		}
		return undefined;
	}

	asExternalDestination(): Writable<ExternalDestination> | undefined {
		if (this._asset.data.case === "externalDestination") {
			return this.store as Writable<ExternalDestination>;
		}
		return undefined;
	}

	asCodeBatch(): Writable<Batch> | undefined {
		// this is ok because the batches are json compatible
		if (this._asset.data.case === "codeBatch" || this._asset.data.case === "entrypointBatch") {
			return this.store as Writable<Batch>;
		}
		return undefined;
	}

	asEntrypointBatch(): Writable<EntrypointBatch> | undefined {
		// this is ok because the batches are json compatible
		if (this._asset.data.case === "codeBatch" || this._asset.data.case === "entrypointBatch") {
			return this.store as Writable<EntrypointBatch>;
		}
		return undefined;
	}

	asEntrypointBatchRef(): Writable<EntryPointBatchRef> | undefined {
		if (this._asset.data.case === "entrypointBatchRef") {
			return this.store as Writable<EntryPointBatchRef>;
		}
		return undefined;
	}

	asExperiment(): Writable<Experiment> | undefined {
		if (this._asset.data.case === "experiment") {
			return this.store as Writable<Experiment>;
		}
		return undefined;
	}

	asPolicyRef(): Writable<PolicyRef> | undefined {
		if (this._asset.data.case === "policyRef") {
			return this.store as Writable<PolicyRef>;
		}
		return undefined;
	}

	asPolicy(): Writable<Policy> | undefined {
		if (this._asset.data.case === "policy") {
			return this.store as Writable<Policy>;
		}
		return undefined;
	}

	asAgent(): Writable<Agent> | undefined {
		if (this._asset.data.case === "agent") {
			return this.store as Writable<Agent>;
		}
		return undefined;
	}

	asLookup(): Writable<Lookup> | undefined {
		if (this._asset.data.case === "lookup") {
			return this.store as Writable<Lookup>;
		}
		return undefined;
	}

	asAdUnit(): Writable<AdUnit> | undefined {
		if (this._asset.data.case === "adUnit") {
			return this.store as Writable<AdUnit>;
		}
		return undefined;
	}

	asAdUnitConfig(): Writable<AdUnitConfig> | undefined {
		if (this._asset.data.case === "adUnitConfig") {
			return this.store as Writable<AdUnitConfig>;
		}
		return undefined;
	}

	equals(other: AssetLoad | undefined): boolean {
		if (this._asset.key !== other?._asset.key) {
			return false;
		}
		if (!equals(AssetSchema, this._asset, other._asset)) {
			return false;
		}
		if (!deepEqual(this._data, other._data)) {
			return false;
		}
		return true;
	}

	toJson(): AssetLoadJson {
		return {
			asset: toJson(AssetSchema, this._asset) ?? {},
			data: deepClone(this._data) ?? {},
			result: deepClone(this._result) ?? {},
		};
	}

	static fromJson(jsonValue: AssetLoadJson): AssetLoad {
		const asset: Asset = jsonValue["asset"] ? fromJson(AssetSchema, jsonValue["asset"], { ignoreUnknownFields: true }) : create(AssetSchema);
		const data: JsonObject = jsonValue["data"] ? (deepClone(jsonValue["data"]) as JsonObject) : {};
		const result: JsonObject = jsonValue["result"] ? (deepClone(jsonValue["result"]) as JsonObject) : {};
		return new AssetLoad(asset, data, result);
	}
}

export function equalAssets(a: Record<string, AssetLoad>, b: Record<string, AssetLoad>): boolean {
	const aKeys = new Set(Object.keys(a));
	const bKeys = new Set(Object.keys(b));
	if (aKeys.size !== bKeys.size) {
		return false;
	}
	for (const key of aKeys) {
		if (!bKeys.has(key)) {
			return false;
		}
		if (!a[key].equals(b[key])) {
			return false;
		}
	}
	return true;
}

export function equalModules(a: Record<string, Module>, b: Record<string, Module>): boolean {
	const aKeys = new Set(Object.keys(a));
	const bKeys = new Set(Object.keys(b));
	if (aKeys.size !== bKeys.size) {
		return false;
	}
	for (const key of aKeys) {
		if (!bKeys.has(key)) {
			return false;
		}
		if (!equals(ModuleSchema, a[key], b[key])) {
			return false;
		}
	}
	return true;
}

export class BundleClient {
	private client: Client<typeof BundleService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.bundles || "/", BundleService, options);
	}

	async listSuites(orgId: string, workspaceId: string, filter?: Filter, pagination?: PaginationArguments, options?: CallOptions): Promise<SuiteList> {
		try {
			const res = await this.client.listSuites(
				{
					orgId,
					workspaceId,
					pagination,
					namePrefix: filter?.namePrefix,
					creatorIds: filter?.creators?.map(u => u.id),
					tags: filter?.tags,
					states: filter?.states,
					categories: filter?.categories,
					folderPath: filter?.folderPath,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listSuiteVersions(orgId: string, workspaceId: string, suiteId: string, state?: AssetState, pagination?: PaginationArguments, options?: CallOptions): Promise<SuiteList> {
		try {
			const res = await this.client.listSuiteVersions(
				{
					orgId,
					workspaceId,
					suiteId,
					pagination,
					state,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listBundles(orgId: string, workspaceId: string, filter?: Filter, pagination?: PaginationArguments, options?: CallOptions): Promise<BundleList> {
		try {
			const res = await this.client.listBundles(
				{
					orgId,
					workspaceId,
					pagination,
					namePrefix: filter?.namePrefix,
					creatorIds: filter?.creators?.map(u => u.id),
					visibilities: filter?.visibilities,
					tags: filter?.tags,
					states: filter?.states,
					categories: filter?.categories,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listBundleVersions(id: string, orgId: string, workspaceId: string, pagination?: PaginationArguments, options?: CallOptions): Promise<BundleList> {
		try {
			const res = await this.client.listBundleVersions(
				{
					id,
					orgId,
					workspaceId,
					paginate: pagination,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getBundleCategories(orgId: string, workspaceId: string, options?: CallOptions): Promise<Category[]> {
		try {
			const res = await this.client.getBundleCategories(
				{
					orgId,
					workspaceId,
				},
				options,
			);
			return res.categories;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getBundle(id: string, version?: string): Promise<Bundle> {
		try {
			const res = await this.client.getBundle({ id, version });
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createBundle(bundle: Bundle): Promise<Bundle> {
		try {
			const res = await this.client.createBundle({ ...genericize(bundle) });
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createBundleFromSuite(suiteId: string, name: string, suiteVersion?: string, description?: string, category?: string): Promise<Bundle> {
		try {
			const res = await this.client.createBundleFromSuite({ suiteId, suiteVersion, name, description, category });
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateBundle(bundle: Bundle, mask: FieldMask): Promise<Bundle> {
		try {
			const res = await this.client.updateBundle({ bundle, mask });
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async importBundle(bundle: string, orgId: string, workspaceId: string): Promise<Bundle> {
		try {
			const res = await this.client.importBundle({ bundle, orgId, workspaceId });
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async exportBundle(id: string, version?: string): Promise<string> {
		try {
			const res = await this.client.exportBundle({ id, version });
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async copyBundle(bundle: Bundle): Promise<Bundle> {
		const toCreate = clone(BundleSchema, bundle);
		toCreate.name += " (Copy)";
		return this.createBundle(toCreate);
	}

	async createBundleVersion(id: string, version: string, increment: VersionIncrement): Promise<Bundle> {
		try {
			const res = await this.client.createBundleVersion({ id, baseVersion: version, increment });
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async publishBundleVersion(id: string, version: string, visibility?: Visibilities): Promise<Bundle> {
		try {
			const res = await this.client.publishBundleVersion({ id, version, visibility });
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createSuiteVersion(id: string, version: string, increment: VersionIncrement): Promise<Suite> {
		try {
			const res = await this.client.createSuiteVersion({ id, baseVersion: version, increment });
			return res.suite!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async publishSuiteVersion(id: string, version: string): Promise<Suite> {
		try {
			const res = await this.client.publishSuiteVersion({ id, version });
			return res.suite!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getSuite(id: string, version?: string, options?: CallOptions): Promise<Suite> {
		try {
			const res = await this.client.getSuite({ id, version }, options);
			return res.suite!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async validateSuite(suite: Suite): Promise<ValidationResult> {
		try {
			const res = await this.client.validateSuite({ suite });
			const errors: Record<string, string> = {};
			res.errors.forEach(error => {
				errors[error.field] = error.description;
			});
			return { valid: res.valid, errors };
		} catch (e) {
			throw handleError(e);
		}
	}

	async createSuite(suite: Suite, config?: Record<string, JsonObject>): Promise<Suite> {
		try {
			const res = await this.client.createSuite({ ...genericize(suite), config });
			return res.suite!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async bulkImportSuites(req: BulkImportSuitesRequest): Promise<void> {
		try {
			await this.client.bulkImportSuites(req);
		} catch (e) {
			throw handleError(e);
		}
	}

	async cloneSuite(suite: Suite, cloneOptions?: CloneOptions): Promise<Suite> {
		try {
			const res = await this.client.cloneSuite({ id: suite.id, options: cloneOptions });
			return res.suite!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateSuite(suite: Suite, mask: FieldMask): Promise<Suite> {
		try {
			const res = await this.client.updateSuite({ suite, mask });
			return res.suite!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveBundle(id: string): Promise<Bundle> {
		try {
			const res = await this.client.updateBundle({
				bundle: create(BundleSchema, {
					id,
					state: AssetState.ARCHIVED,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreBundle(id: string): Promise<Bundle> {
		try {
			const res = await this.client.updateBundle({
				bundle: create(BundleSchema, {
					id,
					state: AssetState.ACTIVE,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteBundle(id: string, version?: string): Promise<void> {
		try {
			await this.client.deleteBundle({ id, version });
		} catch (e) {
			throw handleError(e);
		}
	}

	async moveBundle(bundle: Bundle, folder: Folder): Promise<Bundle> {
		try {
			const res = await this.client.updateBundle({
				bundle: create(BundleSchema, {
					id: bundle.id,
					folderPath: folder.path,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["folder_path"],
				}),
			});
			return res.bundle!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveSuite(suite: Suite): Promise<Suite> {
		try {
			const res = await this.client.updateSuite({
				suite: create(SuiteSchema, {
					id: suite.id,
					state: "ASSET_STATE_ARCHIVED",
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.suite!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveSuites(suites: Suite[]): Promise<Suite[]> {
		if (suites.length == 0) {
			return [];
		}
		if (suites.length == 1) {
			return [await this.archiveSuite(suites[0])];
		}
		if (suites.length <= MAX_BULK_SIZE_PER_UPDATE) {
			return await this.bulkUpdateState(suites, AssetState.ARCHIVED);
		}
		const result: Suite[] = [];
		for (let i = 0; i < suites.length; i += MAX_BULK_SIZE_PER_UPDATE) {
			const chunk = suites.slice(i, i + MAX_BULK_SIZE_PER_UPDATE);
			result.push(...(await this.bulkUpdateState(chunk, AssetState.ARCHIVED)));
		}
		return result;
	}

	async restoreSuite(suite: Suite): Promise<Suite> {
		try {
			const res = await this.client.updateSuite({
				suite: create(SuiteSchema, {
					id: suite.id,
					state: AssetState.ACTIVE,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.suite!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreSuites(suites: Suite[]): Promise<Suite[]> {
		if (suites.length == 0) {
			return [];
		}
		if (suites.length == 1) {
			return [await this.restoreSuite(suites[0])];
		}
		if (suites.length <= MAX_BULK_SIZE_PER_UPDATE) {
			return await this.bulkUpdateState(suites, AssetState.ACTIVE);
		}
		const result: Suite[] = [];
		for (let i = 0; i < suites.length; i += MAX_BULK_SIZE_PER_UPDATE) {
			const chunk = suites.slice(i, i + MAX_BULK_SIZE_PER_UPDATE);
			result.push(...(await this.bulkUpdateState(chunk, AssetState.ACTIVE)));
		}
		return result;
	}

	async bulkUpdateState(suites: Suite[], state: AssetState): Promise<Suite[]> {
		const requests: UpdateSuiteRequest[] = suites.map(suite => {
			return {
				suite: create(SuiteSchema, {
					id: suite.id,
					state,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			} as UpdateSuiteRequest;
		});
		try {
			const result: Suite[] = [];
			const error = new BulkError([], []);
			let i = 0;
			for await (const item of this.client.bulkUpdateSuite({ requests })) {
				if (!item.status) {
					result.push(item.response!.suite!);
				} else {
					error.failed.push(suites[i].id);
					error.errors.push(new APIError(item.status!.message, item.status!.code));
				}
				i++;
			}
			if (error.failed.length > 0) {
				throw error;
			}
			return result;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteSuite(id: string, version?: string): Promise<void> {
		try {
			await this.client.deleteSuite({ id, version });
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteSuites(ids: string[]): Promise<void> {
		if (ids.length == 0) {
			return;
		}
		if (ids.length == 1) {
			return await this.deleteSuite(ids[0]);
		}
		if (ids.length <= MAX_BULK_SIZE_PER_UPDATE) {
			return await this.bulkDeleteSuites(ids);
		}
		for (let i = 0; i < ids.length; i += MAX_BULK_SIZE_PER_UPDATE) {
			const chunk = ids.slice(i, i + MAX_BULK_SIZE_PER_UPDATE);
			await this.bulkDeleteSuites(chunk);
		}
	}

	async bulkDeleteSuites(ids: string[]): Promise<void> {
		const requests: DeleteSuiteRequest[] = ids.map(id => {
			return { id } as DeleteSuiteRequest;
		});
		try {
			const error = new BulkError([], []);
			let i = 0;
			for await (const item of this.client.bulkDeleteSuite({ requests })) {
				if (item.status) {
					error.failed.push(ids[i]);
					error.errors.push(new APIError(item.status!.message, item.status!.code));
				}
				i++;
			}
			if (error.failed.length > 0) {
				throw error;
			}
		} catch (e) {
			throw handleError(e);
		}
	}

	async moveSuite(suite: Suite, folder: Folder, ignoreAnalytics?: boolean): Promise<Suite> {
		try {
			const res = await this.client.updateSuite({
				suite: create(SuiteSchema, {
					orgId: folder.orgId,
					workspaceId: folder.workspaceId,
					id: suite.id,
					folderPath: folder.path,
				}),
				mask: create(FieldMaskSchema, {
					paths: [...(suite.orgId !== folder.orgId ? ["org_id"] : []), ...(suite.workspaceId !== folder.workspaceId ? ["workspace_id"] : []), "folder_path"],
				}),
				ignoreAnalytics: ignoreAnalytics,
			});
			return res.suite!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async moveSuites(suites: Suite[], folder: Folder, ignoreAnalytics?: boolean): Promise<Suite[]> {
		if (suites.length == 0) {
			return [];
		}
		if (suites.length == 1) {
			return [await this.moveSuite(suites[0], folder, ignoreAnalytics)];
		}
		if (suites.length <= MAX_BULK_SIZE_PER_UPDATE) {
			return await this.bulkMoveSuites(suites, folder, ignoreAnalytics);
		}
		const result: Suite[] = [];
		for (let i = 0; i < suites.length; i += MAX_BULK_SIZE_PER_UPDATE) {
			const chunk = suites.slice(i, i + MAX_BULK_SIZE_PER_UPDATE);
			result.push(...(await this.bulkMoveSuites(chunk, folder, ignoreAnalytics)));
		}
		return result;
	}

	async bulkMoveSuites(suites: Suite[], folder: Folder, ignoreAnalytics?: boolean): Promise<Suite[]> {
		const requests: UpdateSuiteRequest[] = suites.map(suite => {
			return {
				suite: create(SuiteSchema, {
					orgId: folder.orgId,
					workspaceId: folder.workspaceId,
					id: suite.id,
					folderPath: folder.path,
				}),
				mask: create(FieldMaskSchema, {
					paths: [...(suite.orgId !== folder.orgId ? ["org_id"] : []), ...(suite.workspaceId !== folder.workspaceId ? ["workspace_id"] : []), "folder_path"],
				}),
				ignoreAnalytics: ignoreAnalytics,
			} as UpdateSuiteRequest;
		});
		try {
			const result: Suite[] = [];
			const error = new BulkError([], []);
			const i = 0;
			for await (const item of this.client.bulkUpdateSuite({ requests })) {
				if (!item.status) {
					result.push(item.response!.suite!);
				} else {
					error.failed.push(suites[i].id);
					error.errors.push(new APIError(item.status!.message, item.status!.code));
				}
			}
			if (error.failed.length > 0) {
				throw error;
			}
			return result;
		} catch (e) {
			throw handleError(e);
		}
	}

	async load(suite: Suite, entryPoint: EntryPoint, key: string, coords?: GeolocationCoords, mlMetadata?: ConversionMetaData, mode: Mode = Mode.LIVE, options?: CallOptions): Promise<AssetLoad> {
		const geolocation = create(GeoLocationSchema, {
			longitude: Number(coords ? coords[0] : -1),
			latitude: Number(coords ? coords[1] : -1),
		});
		try {
			const res = await this.client.load(
				{
					id: suite.id,
					version: suite.version,
					key,
					entryPoint,
					mode,
					mlMetadata,
					geolocation,
				},
				options,
			);
			return new AssetLoad(res.asset ?? create(AssetSchema), res.data);
		} catch (e) {
			throw handleError(e);
		}
	}

	async preload(suite: Suite, entryPoint: EntryPoint, keys: string[], coords?: GeolocationCoords, mlMetadata?: ConversionMetaData, mode: Mode = Mode.LIVE, intent: RequestIntent = RequestIntent.DISPLAY, options?: CallOptions): Promise<AssetLoad[]> {
		if (keys.length === 0) {
			return [];
		}
		const geolocation = create(GeoLocationSchema, {
			longitude: Number(coords ? coords[0] : -1),
			latitude: Number(coords ? coords[1] : -1),
		});
		try {
			const res = this.client.preload(
				{
					id: suite.id,
					version: suite.version,
					keys,
					entryPoint,
					mode,
					mlMetadata,
					geolocation,
					intent,
				},
				options,
			);
			const results: AssetLoad[] = [];
			for await (const item of res) {
				if (item.result.case === "response") {
					const value = item.result.value;
					if (value.asset) {
						results.push(new AssetLoad(value.asset, value.data));
					}
				}
			}
			return results;
		} catch (e) {
			throw handleError(e);
		}
	}

	async edit(suite: Suite, entryPoint: EntryPoint, key: string): Promise<AssetLoad> {
		try {
			const res = await this.client.edit({
				id: suite.id,
				version: suite.version,
				key,
				entryPoint,
			});
			return new AssetLoad(res.asset!, res.data);
		} catch (e) {
			throw handleError(e);
		}
	}

	async editPreload(suite: Suite, entryPoint: EntryPoint, keys: string[]): Promise<AssetLoad[]> {
		try {
			const res = await this.client.editPreload({
				id: suite.id,
				version: suite.version,
				keys,
				entryPoint,
			});
			const results: AssetLoad[] = [];
			for await (const item of res) {
				if (item.result.case === "response") {
					const value = item.result.value;
					results.push(new AssetLoad(value.asset!, value.data));
				}
			}
			return results;
		} catch (e) {
			throw handleError(e);
		}
	}

	async rawExecute(suite: Suite, entryPoint: EntryPoint, key: string, conversionType: ConversionType = ConversionType.UNSPECIFIED, conversionComponent?: string, coords?: GeolocationCoords, data?: JsonObject, metadata?: ConversionMetaData, mode: Mode = Mode.LIVE, options?: CallOptions): Promise<Destination | undefined> {
		const geolocation = create(GeoLocationSchema, {
			longitude: Number(coords ? coords[0] : -1),
			latitude: Number(coords ? coords[1] : -1),
		});
		try {
			const res = await this.client.execute(
				{
					id: suite.id,
					version: suite.version,
					key,
					conversionComponent,
					conversionType,
					data,
					entryPoint,
					metadata,
					mode,
					geolocation,
				},
				options,
			);
			return res.destination;
		} catch (e) {
			throw handleError(e);
		}
	}

	async execute(suite: Suite, entryPoint: EntryPoint, key: string, conversionType: ConversionType, conversionComponent: string, sourceAnchor: string | undefined, coords?: GeolocationCoords, data?: JsonObject, metadata?: ConversionMetaData, mode: Mode = Mode.LIVE, intent: RequestIntent = RequestIntent.DISPLAY, options?: CallOptions): Promise<AssetLoad | string | undefined> {
		const geolocation = create(GeoLocationSchema, {
			longitude: Number(coords ? coords[0] : -1),
			latitude: Number(coords ? coords[1] : -1),
		});
		try {
			const res = await this.client.execute(
				{
					id: suite.id,
					version: suite.version,
					key,
					conversionType,
					conversionComponent,
					sourceAnchor,
					data,
					entryPoint,
					metadata,
					mode,
					geolocation,
					intent,
				},
				options,
			);
			if (res.destination?.value.case == "externalUrl") {
				return res.destination!.value.value as string;
			}
			return new AssetLoad((res.destination?.value.value as Asset) ?? create(AssetSchema, { key }), res.data, res.result);
		} catch (e) {
			throw handleError(e);
		}
	}

	async cancel(suite: Suite, entryPoint: EntryPoint, key: string, coords?: GeolocationCoords, metadata?: ConversionMetaData, mode: Mode = Mode.LIVE, options?: CallOptions): Promise<void> {
		const geolocation = create(GeoLocationSchema, {
			longitude: Number(coords ? coords[0] : -1),
			latitude: Number(coords ? coords[1] : -1),
		});
		try {
			await this.client.cancel(
				{
					suiteId: suite.id,
					version: suite.version,
					key,
					entryPoint,
					metadata,
					mode,
					geolocation,
				},
				options,
			);
		} catch (e) {
			throw handleError(e);
		}
	}

	async exit(suite: Suite, entryPoint: EntryPoint, key: string, coords?: GeolocationCoords, metadata?: ConversionMetaData, mode: Mode = Mode.LIVE, options?: CallOptions): Promise<void> {
		const geolocation = create(GeoLocationSchema, {
			longitude: Number(coords ? coords[0] : -1),
			latitude: Number(coords ? coords[1] : -1),
		});
		try {
			await this.client.exit(
				{
					suiteId: suite.id,
					version: suite.version,
					key,
					entryPoint,
					metadata,
					mode,
					geolocation,
				},
				options,
			);
		} catch (e) {
			throw handleError(e);
		}
	}

	async save(suite: Suite, key: string, data: JsonObject, asset?: AssetLoad, options?: CallOptions): Promise<AssetLoad> {
		try {
			const res = await this.client.save(
				{
					id: suite.id,
					version: suite.version,
					key: key,
					data: data,
					...(asset != null && { asset: asset.asset() }),
				},
				options,
			);
			return new AssetLoad(res.asset!, res.data);
		} catch (e) {
			throw handleError(e);
		}
	}

	async testCall(suite: Suite, entryPoint: EntryPoint, key: string, req: ApiLookup, options?: CallOptions): Promise<CallResponse> {
		try {
			const res = await this.client.testCall(
				{
					id: suite.id,
					version: suite.version,
					key,
					entryPoint,
					apiLookup: req,
				},
				options,
			);
			const decoder = new TextDecoder("utf-8");
			const str = decoder.decode(res.responseBody);
			return { statusCode: res.statusCode, responseHeaders: res.responseHeaders, responseBody: str };
		} catch (e) {
			throw handleError(e);
		}
	}

	async render(id: string, version: string, entryPoint: EntryPoint, toRender: string[], options?: CallOptions): Promise<string[]> {
		try {
			const res = await this.client.renderStrings(
				{
					id,
					version,
					entryPoint,
					toRender,
				},
				options,
			);
			return res.rendered;
		} catch (e) {
			throw handleError(e);
		}
	}

	async renderStruct(id: string, version: string, entryPoint: EntryPoint, toRender: JsonObject, data?: Data, options?: CallOptions): Promise<JsonObject | undefined> {
		try {
			const res = await this.client.renderStruct(
				{
					id,
					version,
					entryPoint,
					toRender,
					data,
				},
				options,
			);
			return res.rendered;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createModule(suite: Suite, template: ModuleTemplate, name: string = template.name, description: string | undefined = template.description, config?: Record<string, JsonObject>): Promise<Module> {
		try {
			const res = await this.client.createModule({
				templateId: template.id,
				templateVersion: template.version,
				suiteId: suite.id,
				suiteVersion: suite.version,
				state: AssetState.DRAFT,
				name,
				description,
				config,
			});
			return res.module!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateModule(module: Module, mask: FieldMask): Promise<Module> {
		try {
			const res = await this.client.updateModule({
				module,
				mask,
			});
			return res.module!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getModule(id: string, version: string): Promise<Module> {
		try {
			const res = await this.client.getModule({ id, version });
			return res.module!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getModulesInSuite(suiteId: string, version?: string): Promise<Record<string, Module>> {
		try {
			const res = await this.client.getModulesInSuite({ suiteId, version });
			return res.modules!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getReferencedSuites(suiteId: string, version?: string): Promise<Record<string, Suite>> {
		try {
			const res = await this.client.getReferencedSuites({ suiteId, version });
			return res.suites!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getModuleTemplate(id: string): Promise<ModuleTemplate> {
		try {
			const res = await this.client.getModuleTemplate({ id });
			return res.template!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getModuleTemplateCategories(orgId: string, workspaceId: string, options?: CallOptions): Promise<Category[]> {
		try {
			const res = await this.client.getModuleTemplateCategories(
				{
					orgId,
					workspaceId,
				},
				options,
			);
			return res.categories;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listModuleTemplates(orgId: string, workspaceId?: string, filter?: Filter, pagination?: PaginationArguments, options?: CallOptions): Promise<ModuleTemplateList> {
		try {
			const res = await this.client.listModuleTemplates({ orgId, workspaceId, pagination, namePrefix: filter?.namePrefix, creatorIds: filter?.creators?.map(u => u.id), visibilities: filter?.visibilities, tags: filter?.tags, states: filter?.states, categories: filter?.categories }, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}
}
